import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock } from 'lucide-react';
import { usePersonStore } from '../../store/personStore';
import { useUserStore } from '../../store/userStore';
import { useAuthStore } from '../../store/authStore';
import { getBookUserId } from '../../lib/actingFor';
import type { PersonDetail, PersonCreate, PersonUpdate, Stage, Temperature } from '../../types';
import toast from 'react-hot-toast';
import { TEMPERATURE_STYLES, TEMPERATURE_ORDER } from '../shared/temperatureConfig';
import { PersonDedupeCheck, type PersonMatch } from './PersonDedupeCheck';

interface PersonFormProps {
  person?: PersonDetail | null; // If provided, form is in edit mode
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (person: PersonDetail) => void;
}

// Stage options
const STAGES: { value: Stage; label: string }[] = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CHURNED', label: 'Churned' },
];

// Position suggestions
const POSITION_SUGGESTIONS = [
  'Head Coach',
  'Assistant Coach',
  'Athletic Director',
  'Associate AD',
  'Coordinator',
  'Booster Club President',
];

// Source suggestions
const SOURCE_SUGGESTIONS = [
  'Manual Entry',
  'Conference Lead',
  'Referral',
  'Website Inquiry',
  'Trade Show',
  'Cold Outreach',
];

/**
 * Person create/edit form modal
 */
export const PersonForm = ({ person, isOpen, onClose, onSuccess }: PersonFormProps) => {
  const { createPerson, updatePerson, isSaving, saveError, clearSaveError } = usePersonStore();
  const { users, fetchUsers } = useUserStore();
  const { user: currentUser } = useAuthStore();
  const bookUserId = getBookUserId(currentUser);
  const navigate = useNavigate();

  const handleDedupeMatch = (match: PersonMatch) => {
    // No org context on the People page — route the rep to the existing
    // person's detail page so they can review and act.
    onClose();
    navigate(`/people/${match.id}`);
  };

  const isEditMode = !!person;
  const canReassign = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    crmFirstName: '',
    crmLastName: '',
    crmEmail: '',
    crmPhone: '',
    position: '',
    source: '',
    temperature: '' as Temperature | '',
    stage: 'LEAD' as Stage,
    status: '',
    personalNote: '',
    nextFollowUpAt: '',
    twitterHandle: '',
    instagramHandle: '',
    linkedInUrl: '',
    assignedUserId: '',
    manuallyAssigned: false,
  });

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch users for assignment dropdown
  useEffect(() => {
    if (isOpen && canReassign) {
      fetchUsers();
    }
  }, [isOpen, canReassign, fetchUsers]);

  // Reset form when person changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (person) {
        // Edit mode - populate with person data.
        // The four overlap fields (firstName/lastName/email/phone) are owned by
        // Blast and rendered read-only. Reps edit them via the parallel
        // crm{First,Last,Email,Phone} override fields, which sync never
        // overwrites. We still populate the canonical fields here so the
        // read-only "From Blast" rows can display them.
        setFormData({
          firstName: person.firstName || '',
          lastName: person.lastName || '',
          email: person.email || '',
          phone: person.phone || '',
          crmFirstName: person.crmFirstName || '',
          crmLastName: person.crmLastName || '',
          crmEmail: person.crmEmail || '',
          crmPhone: person.crmPhone || '',
          position: person.position || '',
          source: person.source || '',
          temperature: person.temperature || '',
          stage: person.stage || 'LEAD',
          status: person.status || '',
          personalNote: person.personalNote || '',
          nextFollowUpAt: person.nextFollowUpAt
            ? new Date(person.nextFollowUpAt).toISOString().split('T')[0]
            : '',
          twitterHandle: person.twitterHandle || '',
          instagramHandle: person.instagramHandle || '',
          linkedInUrl: person.linkedInUrl || '',
          assignedUserId: person.assignedUserId || '',
          manuallyAssigned: person.manuallyAssigned ?? false,
        });
      } else {
        // Create mode - reset to defaults. crm* fields stay empty here for
        // type-shape consistency; they aren't rendered or sent in create mode
        // (a brand-new person has no Blast canonical to override yet).
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          crmFirstName: '',
          crmLastName: '',
          crmEmail: '',
          crmPhone: '',
          position: '',
          source: 'Manual Entry',
          temperature: '',
          stage: 'LEAD',
          status: '',
          personalNote: '',
          nextFollowUpAt: '',
          twitterHandle: '',
          instagramHandle: '',
          linkedInUrl: '',
          assignedUserId: bookUserId || '',
          manuallyAssigned: false,
        });
      }
      setErrors({});
      clearSaveError();
    }
  }, [isOpen, person, clearSaveError, bookUserId]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle temperature button click
  const handleTemperatureClick = (temp: Temperature) => {
    setFormData((prev) => ({
      ...prev,
      temperature: prev.temperature === temp ? '' : temp,
    }));
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (isEditMode) {
      // In edit mode, firstName/lastName are read-only and guaranteed by
      // Blast — no need to require them. Email isn't edited here either;
      // we validate the override (crmEmail) instead.
      if (formData.crmEmail && !emailRegex.test(formData.crmEmail)) {
        newErrors.crmEmail = 'Invalid email format';
      }
    } else {
      // Create mode: canonical fields are the only ones we collect.
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (formData.email && !emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    if (formData.linkedInUrl && !formData.linkedInUrl.startsWith('http')) {
      newErrors.linkedInUrl = 'LinkedIn URL must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      // Shared, mode-agnostic fields (everything outside the four overlap
      // fields owned by Blast).
      const sharedFields = {
        position: formData.position.trim() || null,
        source: formData.source.trim() || null,
        temperature: (formData.temperature as Temperature) || null,
        stage: formData.stage,
        status: formData.status.trim() || null,
        nextFollowUpAt: formData.nextFollowUpAt || null,
        twitterHandle: formData.twitterHandle.trim() || null,
        instagramHandle: formData.instagramHandle.trim() || null,
        linkedInUrl: formData.linkedInUrl.trim() || null,
        ...(canReassign && formData.assignedUserId
          ? { assignedUserId: formData.assignedUserId }
          : {}),
      };

      let result: PersonDetail;

      if (isEditMode && person) {
        // Edit-mode payload: never send canonical Blast fields. Sync owns
        // them — including them here would race the next sync. Rep edits to
        // first/last/email/phone go through the crm* override fields.
        const updateData: PersonUpdate = {
          ...sharedFields,
          personalNote: formData.personalNote.trim() || null,
          crmFirstName: formData.crmFirstName.trim() || null,
          crmLastName: formData.crmLastName.trim() || null,
          crmEmail: formData.crmEmail.trim() || null,
          crmPhone: formData.crmPhone.trim() || null,
          manuallyAssigned: formData.manuallyAssigned,
        };
        result = await updatePerson(person.id, updateData);
        toast.success('Person updated successfully');
      } else {
        // Create-mode payload: collect canonical fields only. There's no
        // Blast canonical to override yet on a brand-new manual record.
        const createData: PersonCreate = {
          ...sharedFields,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
        };
        result = await createPerson(createData);
        toast.success('Person created successfully');
      }

      onSuccess?.(result);
      onClose();
    } catch (error) {
      // Error is already set in store and displayed
      console.error('Failed to save person:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEditMode ? 'Edit Person' : 'Add New Person'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error banner */}
            {saveError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                {saveError}
              </div>
            )}

            {/* Basic Info Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Basic Information</h3>

              {isEditMode && person ? (
                <>
                  {/* Edit mode: the four overlap fields render as read-only
                      Blast value + editable override + clear. They sit
                      full-width above the secondary grid. */}
                  <div className="space-y-4 mb-4">
                    {/* First Name — Blast read-only + override */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-md px-3 py-2 mb-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          From Blast (read-only)
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-200">
                          {person.firstName || <span className="italic text-gray-400">(empty)</span>}
                        </div>
                      </div>
                      <label htmlFor="crmFirstName" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Preferred first name (override)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          id="crmFirstName"
                          name="crmFirstName"
                          value={formData.crmFirstName}
                          onChange={handleChange}
                          className="input flex-1"
                          placeholder="Leave blank to use the Blast value"
                        />
                        {formData.crmFirstName && (
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, crmFirstName: '' }))}
                            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        Leave blank to use the Blast value. Sync will never overwrite this.
                      </p>
                    </div>

                    {/* Last Name — Blast read-only + override */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-md px-3 py-2 mb-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          From Blast (read-only)
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-200">
                          {person.lastName || <span className="italic text-gray-400">(empty)</span>}
                        </div>
                      </div>
                      <label htmlFor="crmLastName" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Preferred last name (override)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          id="crmLastName"
                          name="crmLastName"
                          value={formData.crmLastName}
                          onChange={handleChange}
                          className="input flex-1"
                          placeholder="Leave blank to use the Blast value"
                        />
                        {formData.crmLastName && (
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, crmLastName: '' }))}
                            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        Leave blank to use the Blast value. Sync will never overwrite this.
                      </p>
                    </div>

                    {/* Email — Blast read-only + override */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-md px-3 py-2 mb-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          From Blast (read-only)
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-200">
                          {person.email || <span className="italic text-gray-400">(empty)</span>}
                        </div>
                      </div>
                      <label htmlFor="crmEmail" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Preferred email (override)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          id="crmEmail"
                          name="crmEmail"
                          value={formData.crmEmail}
                          onChange={handleChange}
                          className={`input flex-1 ${errors.crmEmail ? 'border-red-500' : ''}`}
                          placeholder="Leave blank to use the Blast value"
                        />
                        {formData.crmEmail && (
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, crmEmail: '' }))}
                            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {errors.crmEmail && (
                        <p className="mt-1 text-sm text-red-500">{errors.crmEmail}</p>
                      )}
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        Leave blank to use the Blast value. Sync will never overwrite this.
                      </p>
                    </div>

                    {/* Phone — Blast read-only + override */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-md px-3 py-2 mb-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          From Blast (read-only)
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-200">
                          {person.phone || <span className="italic text-gray-400">(empty)</span>}
                        </div>
                      </div>
                      <label htmlFor="crmPhone" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Preferred phone (override)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          id="crmPhone"
                          name="crmPhone"
                          value={formData.crmPhone}
                          onChange={handleChange}
                          className="input flex-1"
                          placeholder="Leave blank to use the Blast value"
                        />
                        {formData.crmPhone && (
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, crmPhone: '' }))}
                            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        Leave blank to use the Blast value. Sync will never overwrite this.
                      </p>
                    </div>
                  </div>

                  {/* Edit mode: secondary fields (Position / Source / Assigned To) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Position */}
                    <div>
                      <label htmlFor="position" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Position
                      </label>
                      <input
                        type="text"
                        id="position"
                        name="position"
                        list="position-suggestions"
                        value={formData.position}
                        onChange={handleChange}
                        className="input"
                        placeholder="Head Coach"
                      />
                      <datalist id="position-suggestions">
                        {POSITION_SUGGESTIONS.map((pos) => (
                          <option key={pos} value={pos} />
                        ))}
                      </datalist>
                    </div>

                    {/* Source */}
                    <div>
                      <label htmlFor="source" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Source
                      </label>
                      <input
                        type="text"
                        id="source"
                        name="source"
                        list="source-suggestions"
                        value={formData.source}
                        onChange={handleChange}
                        className="input"
                        placeholder="Manual Entry"
                      />
                      <datalist id="source-suggestions">
                        {SOURCE_SUGGESTIONS.map((src) => (
                          <option key={src} value={src} />
                        ))}
                      </datalist>
                    </div>

                    {/* Assigned To (ADMIN/MANAGER only) */}
                    {canReassign && (
                      <>
                        <div>
                          <label htmlFor="assignedUserId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Assigned To
                          </label>
                          <select
                            id="assignedUserId"
                            name="assignedUserId"
                            value={formData.assignedUserId}
                            onChange={handleChange}
                            className="input"
                          >
                            <option value="">Select a user...</option>
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.displayName || u.email} ({u.role.replace('_', ' ')})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Manual override pin (admin-visible): when checked, the
                            current assignedUserId is pinned and won't be
                            recomputed when the linked NamedAccount's owner
                            changes. Server-side this maps to Person.manuallyAssigned. */}
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="manuallyAssigned"
                            name="manuallyAssigned"
                            checked={formData.manuallyAssigned}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, manuallyAssigned: e.target.checked }))
                            }
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="manuallyAssigned" className="text-sm text-gray-700 dark:text-gray-300">
                            Pin this assignment (skip auto-recompute on NamedAccount changes)
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                // Create mode: brand-new person, no Blast canonical exists yet,
                // so the four overlap fields are plain editable inputs.
                // First/Last/Email are owned by PersonDedupeCheck (typeahead +
                // email-blur banner against existing people). The remaining
                // fields sit in a smaller grid below.
                <>
                  <PersonDedupeCheck
                    firstName={formData.firstName}
                    lastName={formData.lastName}
                    email={formData.email}
                    onFirstNameChange={(v) => setFormData((prev) => ({ ...prev, firstName: v }))}
                    onLastNameChange={(v) => setFormData((prev) => ({ ...prev, lastName: v }))}
                    onEmailChange={(v) => setFormData((prev) => ({ ...prev, email: v }))}
                    onMatchSelected={handleDedupeMatch}
                    disabled={isSaving}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-500">{errors.firstName}</p>
                  )}
                  {errors.lastName && (
                    <p className="text-sm text-red-500">{errors.lastName}</p>
                  )}
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input"
                        placeholder="555-123-4567"
                      />
                    </div>

                    {/* Position */}
                    <div>
                      <label htmlFor="position" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Position
                      </label>
                      <input
                        type="text"
                        id="position"
                        name="position"
                        list="position-suggestions"
                        value={formData.position}
                        onChange={handleChange}
                        className="input"
                        placeholder="Head Coach"
                      />
                      <datalist id="position-suggestions">
                        {POSITION_SUGGESTIONS.map((pos) => (
                          <option key={pos} value={pos} />
                        ))}
                      </datalist>
                    </div>

                    {/* Source */}
                    <div>
                      <label htmlFor="source" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Source
                      </label>
                      <input
                        type="text"
                        id="source"
                        name="source"
                        list="source-suggestions"
                        value={formData.source}
                        onChange={handleChange}
                        className="input"
                        placeholder="Manual Entry"
                      />
                      <datalist id="source-suggestions">
                        {SOURCE_SUGGESTIONS.map((src) => (
                          <option key={src} value={src} />
                        ))}
                      </datalist>
                    </div>

                    {/* Assigned To (ADMIN/MANAGER only) */}
                    {canReassign && (
                      <div>
                        <label htmlFor="assignedUserId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Assigned To
                        </label>
                        <select
                          id="assignedUserId"
                          name="assignedUserId"
                          value={formData.assignedUserId}
                          onChange={handleChange}
                          className="input"
                        >
                          <option value="">Select a user...</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.displayName || u.email} ({u.role.replace('_', ' ')})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Pipeline Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Pipeline Status</h3>
              <div className="space-y-4">
                {/* Temperature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Temperature
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                    {TEMPERATURE_ORDER.map((value) => {
                      const style = TEMPERATURE_STYLES[value];
                      const { Icon } = style;
                      const isSelected = formData.temperature === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleTemperatureClick(value)}
                          className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border-2 transition-all ${
                            isSelected
                              ? `${style.selectedBorder} ${style.selectedBg} ${style.selectedText} font-medium`
                              : 'border-gray-200 hover:border-gray-300 text-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-300'
                          }`}
                        >
                          <Icon className={`h-4 w-4 ${isSelected ? style.iconClass : 'text-gray-400 dark:text-gray-500'}`} />
                          {style.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Stage */}
                  <div>
                    <label htmlFor="stage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Stage
                    </label>
                    <select
                      id="stage"
                      name="stage"
                      value={formData.stage}
                      onChange={handleChange}
                      className="input"
                    >
                      {STAGES.map((stage) => (
                        <option key={stage.value} value={stage.value}>
                          {stage.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Next Follow-up */}
                  <div>
                    <label htmlFor="nextFollowUpAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Next Follow-up
                    </label>
                    <input
                      type="date"
                      id="nextFollowUpAt"
                      name="nextFollowUpAt"
                      value={formData.nextFollowUpAt}
                      onChange={handleChange}
                      className="input"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status / Next Action
                  </label>
                  <textarea
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    rows={2}
                    className="input"
                    placeholder="Waiting for callback, sent proposal, etc."
                  />
                </div>

                {/* Private Note — edit existing only; visibility is server-gated to owner/manager/admin */}
                {isEditMode && (
                  <div>
                    <label htmlFor="personalNote" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Lock className="h-3.5 w-3.5 text-gray-400" />
                      Private Notes
                    </label>
                    <textarea
                      id="personalNote"
                      name="personalNote"
                      value={formData.personalNote}
                      onChange={handleChange}
                      rows={4}
                      maxLength={5000}
                      className="input"
                      placeholder="Private context — visible only to you and your manager."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Social Media (Optional)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Twitter */}
                <div>
                  <label htmlFor="twitterHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Twitter
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      @
                    </span>
                    <input
                      type="text"
                      id="twitterHandle"
                      name="twitterHandle"
                      value={formData.twitterHandle}
                      onChange={handleChange}
                      className="input pl-8"
                      placeholder="username"
                    />
                  </div>
                </div>

                {/* Instagram */}
                <div>
                  <label htmlFor="instagramHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Instagram
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      @
                    </span>
                    <input
                      type="text"
                      id="instagramHandle"
                      name="instagramHandle"
                      value={formData.instagramHandle}
                      onChange={handleChange}
                      className="input pl-8"
                      placeholder="username"
                    />
                  </div>
                </div>

                {/* LinkedIn */}
                <div>
                  <label htmlFor="linkedInUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    LinkedIn URL
                  </label>
                  <input
                    type="url"
                    id="linkedInUrl"
                    name="linkedInUrl"
                    value={formData.linkedInUrl}
                    onChange={handleChange}
                    className={`input ${errors.linkedInUrl ? 'border-red-500' : ''}`}
                    placeholder="https://linkedin.com/in/..."
                  />
                  {errors.linkedInUrl && (
                    <p className="mt-1 text-sm text-red-500">{errors.linkedInUrl}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Saving...
                  </>
                ) : isEditMode ? (
                  'Save Changes'
                ) : (
                  'Create Person'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
